<script setup>
import {ref, watch} from "vue";
import {createCSV, download} from "@/helpers/file";
import AiChatDebugPanel from "@/components/aiChat/AiChatDebugPanel.vue";
import AiChatLoader from "@/components/aiChat/AiChatLoader.vue";
import useAiChat from "@/composables/useAiChat";

const showModal = ref(false);
const content = ref(null);

const roundFormat = {
    question: '',
    answer: '',
    output: null
};

const params = {
    url: '/chat-stream/get-reporting-ai-response',
    roundFormat,
    content,
}

const {
    question,
    rounds,
    loading,
    systemMessages,
    loaderMessage,
    clearStream,
    sendRequest,
    sanitizeAnswer,
    reset,
} = useAiChat(params);

function downloadFile(file) {
    let columns = file.columns.reduce((acc, column) => {
        acc[column.column] = column.label;
        return acc;
    }, {});

    let data = file.data.map(item =>
        file.columns.reduce((row, column) => {
            row[column.column] = item[column.column];
            return row;
        }, {})
    );

    download(createCSV(data, columns), 'response.csv');
}

// ensure we always close stream when modal is hidden or component unmounts
watch(showModal, (value) => {
    if (!value) {
        clearStream();
    }
});

function open(predefinedQuestion) {
    reset();
    question.value = predefinedQuestion;
    showModal.value = true;
    sendRequest();
}

defineExpose({
    open
});
</script>

<template>
    <b-modal v-model="showModal" hide-footer hide-header modal-class="modal-ai-chat">
        <!-- <a href="#" class="close">Back</a> -->
        <section class="section-ai-chat" ref="content">
            <button @click="showModal = false" type="button" aria-label="Close" class="close"></button>

            <div class="ai-chat__inner">
                <div class="ai-chat__content">
                    <!-- <h5 class="heading5 heading5--secondary">Find VCs, accelerators, universities, incubators and more.</h5> -->

                    <div class="ai-chat">
                        <div
                            v-for="(round, index) in rounds"
                            :key="index"
                            class="ai-chat__inner"
                        >
                            <div class="ai-chat__user">
                                {{round.question}}
                            </div>

                            <div class="ai-chat__machine" :class="{'long-div': index === rounds.length - 1}">
                                <div v-if="loaderMessage.length && index === rounds.length -1">
                                    <AiChatLoader :message="loaderMessage"/>
                                </div>

                                <p v-html="sanitizeAnswer(round.answer)"/>

                                <table class="ai-chat__table" v-if="round.output?.table">
                                    <thead>
                                    <tr>
                                        <th v-for="column in round.output.table.columns" :key="column.column">
                                            {{ column.label }}
                                        </th>
                                    </tr>
                                    </thead>

                                    <tbody>
                                    <tr v-for="(row, rowIndex) in round.output.table.data" :key="rowIndex">
                                        <td v-for="column in round.output.table.columns" :key="column.column">
                                            <a
                                                v-if="round.output.table.company_data_fields && round.output.table.company_data_fields.name === column.column"
                                                :href="`/startups/${row[round.output.table.company_data_fields.id]}`"
                                                target="_blank"
                                            >
                                                {{ row[round.output.table.company_data_fields.name] }}
                                            </a>

                                            <template v-else>
                                                {{ row[column.column] }}
                                            </template>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>

                                <button
                                    v-if="round.output?.file"
                                    class="button-csv"
                                    @click="downloadFile(round.output.file)"
                                >
                                    Download CSV
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="ai-chat__footer">
                        <div v-if="!loading" class="card--ai__search">
                            <textarea
                                v-focus
                                v-model="question"
                                placeholder="Ask about startups or partnerships"
                                class="card--ai__search__input"
                                @keyup.enter.prevent="sendRequest"
                            />

                            <button class="card--ai__search__button" @click="sendRequest"></button>
                        </div>
                    </div>

                    <!-- Dev debug zone: invisible click trigger in bottom-left corner -->
                    <AiChatDebugPanel
                        :system-messages="systemMessages"
                    />
                </div>
            </div>
        </section>
    </b-modal>
</template>

<style scoped>
.long-div {
    min-height: 140px;
}

.section-ai-chat {
    overflow-y: auto;
    scroll-behavior: smooth;
    padding-bottom: 160px;
}

.ai-chat__footer {
    pointer-events: none;
}

.ai-chat__footer .card--ai__search {
    pointer-events: auto;
}
</style>
